import { AfterViewInit, ChangeDetectorRef, Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { BdoApiService } from '../../../services/bdo-api.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Location } from '@angular/common';
import { ALLOWED_KEYS, StorageService } from '../../../services/storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { TrackingService } from '../../../services/tracking.service';
import { TRACKING } from '../../../enums/trackingParts.enum';
import { INPUT_TYPE } from '../../../enums/inputType.enum';
import { ContractPaymentData, ValidPromotionCodeResponse } from '../../../../../assets/js/com/ts_api_client';
import { MovePaymentFormData } from '../../../models/movePaymentFormData';
import { of, timer } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { PaymentFormtype } from '../../../../shared/formtypes/payment-formtype';
import { KameleoonService } from '../../../services/kameleoon.service';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'bdo-payment',
    templateUrl: './payment.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class PaymentComponent implements OnInit, AfterViewInit {

  public TRACKING = TRACKING;
  public promoSuccessText: string = '';

  // @Short-Term-Todo: outsourcing; (maybe) mapping should be automatically detected
  public inputTypeMappings = {
    'paymentMethod': INPUT_TYPE.RADIO,
    'sepaAuth': INPUT_TYPE.CHECKBOX,
  }; // Needed for Tracking


  public paymentForm = new FormGroup<PaymentFormtype>({
    paymentMethod: new FormControl('sepa', { validators: [Validators.required] }),
    promotionCode: new FormControl('', { asyncValidators: [
      (control) => {
        const code = control.value?.trim();
        this.promoSuccessText = '';
        if (!code) {
          if (control.dirty) {
            StorageService.setPromoCode(undefined);
          }
          return of(null);
        }
        return timer(500).pipe(
          switchMap(() => this.apiService.verifyPromotionCode(code).pipe(
            catchError(() => of(null))
          )),
          map(response => {
            if (response?.valid) {
              this.trackingService.postSimpleTracking(TRACKING.LOCATION.PROMOCODE, TRACKING.ACTION.SUCCESS);
              this.promoSuccessText = (response as ValidPromotionCodeResponse).promotion?.successText ?? '';
              StorageService.setPromoCode(code);
              return null;
            } else {
              this.promoSuccessText = '';
              this.trackingService.postSimpleTracking(TRACKING.LOCATION.PROMOCODE, TRACKING.ACTION.ERROR);
              StorageService.setPromoCode(undefined);
              return { invalidCode: this.translateService.instant('payment.promoCode.invalid')  };
            }
          })
        );
      }
    ] }),
  });

  constructor(
    public trackingService: TrackingService,
    public apiService: BdoApiService,
    public location: Location,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private kameleoonService: KameleoonService,
    private translateService: TranslateService
  ) {
  }

  ngOnInit(): void {
    this.trackingService.postTracking(TRACKING.LOCATION.DELIVERY_PAYMENT, TRACKING.ACTION.ENTER);

    // update form when paymentMethod is chosen
    this.paymentForm.get('paymentMethod').valueChanges.subscribe({ next: value => {
      this.sepaUpdate(value);
      this.changeDetector.detectChanges();
    } });

  }

  ngAfterViewInit() {

    // prefill form, if data is available
    if (StorageService.getValue(ALLOWED_KEYS.PAYMENT_DATA)) {
      const data = StorageService.getValue<MovePaymentFormData>(ALLOWED_KEYS.PAYMENT_DATA);
      if (data?.paymentMethod === 'bankTransfer') {
        this.paymentForm.get('paymentMethod').setValue('bankTransfer');
      } else {
        this.paymentForm.setValue(data);
      }
      this.changeDetector.detectChanges();
    }

    const savedPromoCode = StorageService.getPromoCode();
    if (savedPromoCode) {
      this.paymentForm.get('promotionCode').setValue(savedPromoCode, { emitEvent: false });
      this.paymentForm.get('promotionCode').markAsDirty();
    }

  }

  /**
   * setup validation if sepa is (not) chosen
   *
   * @param value
   */
  sepaUpdate(value: string) {
    if (value !== 'sepa') {
      this.paymentForm.removeControl('sepaAuth');
      this.paymentForm.removeControl('bankData');
    }
  }

  /**
   * @Short-Term-TODO: outsourcing for reusability
   *
   * @param inputName
   */
  trackInvalid(inputName: string) {
    const formControl = this.paymentForm.get(inputName);
    const inputType = this.inputTypeMappings[inputName];

    if (formControl.invalid) {
      this.trackFormError(inputName, inputType);
    }
  }

  /**
   * * @Short-Term-TODO: outsourcing for reusability
   *
   * @param inputName
   * @param inputType
   */
  trackFormError(inputName: string, inputType: string) {
    this.trackingService.postFormTracking(
      TRACKING.FORM.DELIVERY_PAYMENTDATA,
      TRACKING.FORM_ACTION.ERROR,
      TRACKING.FORM.DELIVERY_PAYMENTDATA,
      inputName,
      inputType
    );
  }


  /**
   * submit-process
   * validate, track, save and go to next step
   */
  public save(): void {

    if (this.paymentForm.invalid) {
      // mark all input fields as touched to display validation info
      this.paymentForm.markAllAsTouched();

      this.trackingService.postSimpleTracking(TRACKING.FORM.DELIVERY_PAYMENTDATA, ' invalid');
      // Iterate over all invalid values
      Object.keys(this.paymentForm.controls).forEach(key => {
        this.trackInvalid(key);
      });
      return;
    }
    this.trackingService.postFormTracking(TRACKING.FORM.DELIVERY_PAYMENTDATA, TRACKING.FORM_ACTION.SUBMIT);
    this.trackingService.postSimpleTracking(
      TRACKING.LOCATION.DELIVERY_PAYMENT, TRACKING.FORM_ACTION.SELECT, 'Payment.' + this.paymentForm.get('paymentMethod').value
    );
    this.trackingService.postSimpleTracking(TRACKING.LOCATION.DELIVERY_PAYMENT, TRACKING.ACTION.GOTO + ' zusammenfassung');

    // Save Data and goto next page (when all data is set)
    const formData: ContractPaymentData = {};
    Object.keys(this.paymentForm.controls).forEach(key => {
      formData[key] = this.paymentForm.controls[key].value;
    });

    StorageService.setValue(ALLOWED_KEYS.PAYMENT_DATA, formData);

    if (!this.paymentForm.get('promotionCode').value?.trim()) {
      StorageService.setPromoCode(undefined);
    }
    // Note: if async validation is still PENDING, the stored value may not yet be set.
    // The async validator handles storage writes on completion.

    this.kameleoonService.processConversion(KameleoonService.Goals.DELIVERY_BANKDATA_DONE);

    this.router.navigate(['../uebersicht'], { relativeTo: this.activatedRoute });
  }

}
